import { useState } from 'react';

import { Dropdown, TabPage, TabView } from 'fluxo-ui';
import { ArrowUpIcon, BarChartIcon, LineChartUpIcon } from 'fluxo-ui/icons';

import { BarChart } from '../charts';

import type { ComponentEvent, ListItem } from 'fluxo-ui';

interface QuestionAnalytics {
    questionId: string;
    questionNumber: number;
    questionTitle: string;
    questionType: string;
    maxMarks: number;
    attempts: number;
    avgScore: number;
    avgTimeSeconds: number;
    correctCount: number;
    partialCount: number;
    incorrectCount: number;
    difficultyIndex: number;
    discriminationIndex: number;
}

interface AnalyticsProps {
    examTitle: string;
    totalAttempts: number;
    avgScore: number;
    passRate: number;
    avgTimeSeconds: number;
    scoreDistribution: {
        range: string;
        count: number;
        percentage: number;
    }[];
    questions: QuestionAnalytics[];
}

const Analytics: React.FC<AnalyticsProps> = ({
    examTitle,
    totalAttempts,
    avgScore,
    passRate,
    avgTimeSeconds,
    scoreDistribution,
    questions,
}) => {
    const [selectedMetric, setSelectedMetric] = useState<string>('difficulty');

    const metricOptions: ListItem[] = [
        { label: 'Difficulty Index', value: 'difficulty' },
        { label: 'Discrimination Index', value: 'discrimination' },
        { label: 'Average Score', value: 'avgScore' },
        { label: 'Average Time', value: 'avgTime' },
    ];

    const formatTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    // Difficulty index (0-1): proportion answering correctly. Higher = easier.
    const getDifficultyChip = (index: number) => {
        if (index >= 0.8)
            return { label: 'Very Easy', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' };
        if (index >= 0.6) return { label: 'Easy', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' };
        if (index >= 0.4) return { label: 'Medium', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' };
        if (index >= 0.2) return { label: 'Hard', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' };
        return { label: 'Very Hard', cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' };
    };

    // Discrimination index (0-1): separation of high vs low performers. Higher = better.
    const getDiscriminationChip = (index: number) => {
        if (index >= 0.4)
            return { label: 'Excellent', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' };
        if (index >= 0.3) return { label: 'Good', cls: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300' };
        if (index >= 0.2) return { label: 'Fair', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' };
        if (index >= 0.1) return { label: 'Poor', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' };
        return { label: 'Very Poor', cls: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' };
    };

    const getMetricValue = (question: QuestionAnalytics) => {
        switch (selectedMetric) {
            case 'difficulty':
                return question.difficultyIndex.toFixed(2);
            case 'discrimination':
                return question.discriminationIndex.toFixed(2);
            case 'avgScore':
                return `${((question.avgScore / question.maxMarks) * 100).toFixed(1)}%`;
            case 'avgTime':
                return formatTime(question.avgTimeSeconds);
            default:
                return '-';
        }
    };

    const answerBreakdown = (question: QuestionAnalytics) => [
        { label: 'Correct', count: question.correctCount, barCls: 'bg-emerald-500' },
        { label: 'Partial', count: question.partialCount, barCls: 'bg-amber-500' },
        { label: 'Incorrect', count: question.incorrectCount, barCls: 'bg-rose-500' },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h2 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">{examTitle}</h2>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Exam Analytics &amp; Performance Insights</p>
            </div>

            <TabView>
                {/* Overview Tab */}
                <TabPage header="Overview" leftIcon={BarChartIcon}>
                    <div className="p-6 space-y-6">
                        {/* Key Metrics */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                                <div className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">{totalAttempts}</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Total Attempts</div>
                            </div>
                            <div className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                                <div className="text-2xl font-bold text-teal-600 dark:text-teal-400 tabular-nums">
                                    {avgScore.toFixed(1)}%
                                </div>
                                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Avg Score</div>
                            </div>
                            <div className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                                <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                                    {passRate.toFixed(1)}%
                                </div>
                                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Pass Rate</div>
                            </div>
                            <div className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
                                <div className="text-2xl font-bold text-cyan-600 dark:text-cyan-400 tabular-nums">
                                    {formatTime(avgTimeSeconds)}
                                </div>
                                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Avg Time</div>
                            </div>
                        </div>

                        {/* Score Distribution */}
                        <div>
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Score Distribution</h3>
                            <BarChart
                                data={scoreDistribution.map((dist) => ({ label: dist.range, value: dist.count }))}
                                formatValue={(v) => `${v}`}
                            />
                        </div>
                    </div>
                </TabPage>

                {/* Question Analytics Tab */}
                <TabPage header="Questions" leftIcon={ArrowUpIcon}>
                    <div className="p-6 space-y-6">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Question Performance</h3>
                            <div className="w-64">
                                <Dropdown
                                    options={metricOptions}
                                    value={selectedMetric}
                                    onChange={(e: ComponentEvent<string>) => setSelectedMetric(e.value)}
                                />
                            </div>
                        </div>

                        <div className="space-y-3">
                            {questions.map((question) => {
                                const difficulty = getDifficultyChip(question.difficultyIndex);
                                const discrimination = getDiscriminationChip(question.discriminationIndex);
                                return (
                                    <div
                                        key={question.questionId}
                                        className="p-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm"
                                    >
                                        <div className="flex items-start justify-between gap-3 mb-3">
                                            <div className="flex items-start gap-3 flex-1 min-w-0">
                                                <span className="w-8 h-8 flex items-center justify-center bg-slate-100 dark:bg-slate-700 rounded-lg font-semibold text-sm text-slate-900 dark:text-white tabular-nums shrink-0">
                                                    {question.questionNumber}
                                                </span>
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-medium text-slate-900 dark:text-white">
                                                        {question.questionTitle}
                                                    </div>
                                                    <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                                                        {question.questionType.replace(/_/g, ' ').toUpperCase()} • {question.maxMarks}{' '}
                                                        marks
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-2 mt-2">
                                                        <span
                                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium tabular-nums ${difficulty.cls}`}
                                                            title="Difficulty index — proportion answering correctly (higher = easier)"
                                                        >
                                                            {difficulty.label} · {question.difficultyIndex.toFixed(2)}
                                                        </span>
                                                        <span
                                                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium tabular-nums ${discrimination.cls}`}
                                                            title="Discrimination index — separation of high vs low performers (higher = better)"
                                                        >
                                                            {discrimination.label} · {question.discriminationIndex.toFixed(2)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <div className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums">
                                                    {getMetricValue(question)}
                                                </div>
                                                <div className="text-xs text-slate-400 dark:text-slate-500">
                                                    {metricOptions.find((m) => m.value === selectedMetric)?.label}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-3 gap-4 pt-3 border-t border-slate-100 dark:border-slate-700/60">
                                            {answerBreakdown(question).map((item) => (
                                                <div key={item.label}>
                                                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">{item.label}</div>
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 bg-slate-100 dark:bg-slate-700 rounded-full h-2">
                                                            <div
                                                                className={`${item.barCls} h-2 rounded-full`}
                                                                style={{
                                                                    width: `${question.attempts > 0 ? (item.count / question.attempts) * 100 : 0}%`,
                                                                }}
                                                            ></div>
                                                        </div>
                                                        <span className="text-sm font-medium text-slate-900 dark:text-white w-12 text-right tabular-nums">
                                                            {item.count}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Info Box */}
                        <div className="p-4 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl">
                            <h4 className="font-semibold text-teal-900 dark:text-teal-100 mb-2">Understanding Metrics</h4>
                            <ul className="space-y-1 text-sm text-teal-800 dark:text-teal-200">
                                <li>
                                    <strong>Difficulty Index:</strong> Proportion of students who answered correctly. Higher = easier.
                                </li>
                                <li>
                                    <strong>Discrimination Index:</strong> How well the question separates high and low performers. Higher =
                                    better discrimination.
                                </li>
                            </ul>
                        </div>
                    </div>
                </TabPage>

                {/* Insights Tab */}
                <TabPage header="Insights" leftIcon={LineChartUpIcon}>
                    <div className="p-6 space-y-4">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Key Insights</h3>

                        {passRate < 50 && (
                            <div className="p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl">
                                <h4 className="font-semibold text-rose-900 dark:text-rose-100 mb-2">Low Pass Rate</h4>
                                <p className="text-sm text-rose-800 dark:text-rose-200">
                                    Only {passRate.toFixed(1)}% of students passed. Consider reviewing the exam difficulty or providing
                                    additional study materials.
                                </p>
                            </div>
                        )}

                        {avgScore < 60 && (
                            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                                <h4 className="font-semibold text-amber-900 dark:text-amber-100 mb-2">Low Average Score</h4>
                                <p className="text-sm text-amber-800 dark:text-amber-200">
                                    The average score is {avgScore.toFixed(1)}%. This might indicate the exam was too challenging for most
                                    students.
                                </p>
                            </div>
                        )}

                        {questions.filter((q) => q.discriminationIndex < 0.1).length > 0 && (
                            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl">
                                <h4 className="font-semibold text-orange-900 dark:text-orange-100 mb-2">Poor Discriminating Questions</h4>
                                <p className="text-sm text-orange-800 dark:text-orange-200 mb-2">
                                    {questions.filter((q) => q.discriminationIndex < 0.1).length} question(s) have poor discrimination.
                                    Consider revising these questions:
                                </p>
                                <ul className="list-disc list-inside text-sm text-orange-800 dark:text-orange-200">
                                    {questions
                                        .filter((q) => q.discriminationIndex < 0.1)
                                        .map((q) => (
                                            <li key={q.questionId}>
                                                Q{q.questionNumber}: {q.questionTitle}
                                            </li>
                                        ))}
                                </ul>
                            </div>
                        )}

                        {passRate >= 80 && avgScore >= 80 && (
                            <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl">
                                <h4 className="font-semibold text-emerald-900 dark:text-emerald-100 mb-2">Excellent Performance</h4>
                                <p className="text-sm text-emerald-800 dark:text-emerald-200">
                                    Students performed very well on this exam with {passRate.toFixed(1)}% pass rate and{' '}
                                    {avgScore.toFixed(1)}% average score.
                                </p>
                            </div>
                        )}
                    </div>
                </TabPage>
            </TabView>
        </div>
    );
};

export default Analytics;
